import { useState } from "react";
import axios from "axios";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./ReportsPage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

type ReportTab = "Bookings" | "Revenue" | "Guests" | "Occupancy";

// ── CSV export ──────────────────────────────────────────────
function exportCSV(filename: string, rows: any[]) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers.map((h) => JSON.stringify(r[h] ?? "")).join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Helpers ─────────────────────────────────────────────────
function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const EGP = (n: number) => `EGP ${(n ?? 0).toLocaleString()}`;

function inRange(dateStr: string | null, from: string, to: string): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr).getTime();
  const f = from ? new Date(from).getTime() : -Infinity;
  const t = to   ? new Date(to).getTime()   : Infinity;
  return d >= f && d <= t;
}

// ── Sub-components ──────────────────────────────────────────
function DateRangePicker({
  from, to,
  onFromChange, onToChange,
}: {
  from: string; to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
}) {
  return (
    <div className="rp-date-row">
      <div className="rp-date-field">
        <label className="rp-date-label">From</label>
        <input
          type="date"
          className="rp-date-input"
          value={from}
          onChange={(e) => onFromChange(e.target.value)}
        />
      </div>
      <div className="rp-date-field">
        <label className="rp-date-label">To</label>
        <input
          type="date"
          className="rp-date-input"
          value={to}
          onChange={(e) => onToChange(e.target.value)}
        />
      </div>
    </div>
  );
}

// ── BOOKINGS TAB ─────────────────────────────────────────────
function BookingsReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/dashboard/bookings`);
      const all: any[] = Array.isArray(res.data) ? res.data : res.data.bookings || res.data.data || [];
      const filtered = all.filter((b) =>
        from || to ? inRange(b.checkIn, from, to) : true
      );
      setRows(
        filtered.map((b) => ({
          BookingID: b.bookingId || b.id,
          Guest: b.guestName,
          Phone: b.phone,
          Room: b.roomId,
          CheckIn: fmt(b.checkIn),
          CheckOut: fmt(b.checkOut),
          Nights: b.nights,
          Total: b.total,
          Status: b.status,
        }))
      );
      setGenerated(true);
    } catch {
      alert("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = rows.reduce((s, r) => s + (Number(r.Total) || 0), 0);

  return (
    <div className="rp-report-body">
      <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      <button className="rp-generate-btn" onClick={generate} disabled={loading}>
        {loading ? "Generating…" : "Generate Report"}
      </button>

      {generated && (
        <>
          <div className="rp-summary-row">
            <div className="rp-summary-chip">
              <span className="rp-summary-label">Total Bookings</span>
              <span className="rp-summary-val">{rows.length}</span>
            </div>
            <div className="rp-summary-chip">
              <span className="rp-summary-label">Total Revenue</span>
              <span className="rp-summary-val">{EGP(totalRevenue)}</span>
            </div>
            <button
              className="rp-export-btn"
              onClick={() => exportCSV("bookings-report.csv", rows)}
              disabled={!rows.length}
            >
              Export CSV
            </button>
          </div>
          {rows.length === 0 ? (
            <div className="rp-empty">No bookings found for this date range</div>
          ) : (
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr>
                    {["BookingID", "Guest", "Phone", "Room", "CheckIn", "CheckOut", "Nights", "Total", "Status"].map((h) => (
                      <th key={h} className="rp-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="rp-tr">
                      <td className="rp-td rp-td-mono">{r.BookingID}</td>
                      <td className="rp-td rp-td-bold">{r.Guest}</td>
                      <td className="rp-td rp-td-muted">{r.Phone}</td>
                      <td className="rp-td">{r.Room}</td>
                      <td className="rp-td">{r.CheckIn}</td>
                      <td className="rp-td">{r.CheckOut}</td>
                      <td className="rp-td">{r.Nights}</td>
                      <td className="rp-td rp-td-accent">{EGP(r.Total)}</td>
                      <td className="rp-td">
                        <span className={`rp-status-badge rp-status-${r.Status?.toLowerCase().replace("-", "")}`}>
                          {r.Status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── REVENUE TAB ──────────────────────────────────────────────
function RevenueReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/dashboard/bookings`);
      const all: any[] = Array.isArray(res.data) ? res.data : res.data.bookings || res.data.data || [];
      const filtered = all.filter((b) =>
        (b.status !== "cancelled") &&
        (from || to ? inRange(b.checkIn, from, to) : true)
      );
      // Group by month
      const byMonth: Record<string, { month: string; bookings: number; revenue: number }> = {};
      for (const b of filtered) {
        const d = b.checkIn;
        const key = d ? new Date(d).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "Unknown";
        if (!byMonth[key]) byMonth[key] = { month: key, bookings: 0, revenue: 0 };
        byMonth[key].bookings += 1;
        byMonth[key].revenue += Number(b.total) || 0;
      }
      setRows(Object.values(byMonth));
      setGenerated(true);
    } catch {
      alert("Failed to fetch revenue data");
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = rows.reduce((s, r) => s + r.revenue, 0);
  const totalBookings = rows.reduce((s, r) => s + r.bookings, 0);

  return (
    <div className="rp-report-body">
      <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      <button className="rp-generate-btn" onClick={generate} disabled={loading}>
        {loading ? "Generating…" : "Generate Report"}
      </button>

      {generated && (
        <>
          <div className="rp-summary-row">
            <div className="rp-summary-chip">
              <span className="rp-summary-label">Total Revenue</span>
              <span className="rp-summary-val">{EGP(totalRevenue)}</span>
            </div>
            <div className="rp-summary-chip">
              <span className="rp-summary-label">Bookings</span>
              <span className="rp-summary-val">{totalBookings}</span>
            </div>
            <button
              className="rp-export-btn"
              onClick={() => exportCSV("revenue-report.csv", rows)}
              disabled={!rows.length}
            >
              Export CSV
            </button>
          </div>
          {rows.length === 0 ? (
            <div className="rp-empty">No revenue data for this period</div>
          ) : (
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr>
                    <th className="rp-th">Month</th>
                    <th className="rp-th">Bookings</th>
                    <th className="rp-th rp-th-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="rp-tr">
                      <td className="rp-td rp-td-bold">{r.month}</td>
                      <td className="rp-td">{r.bookings}</td>
                      <td className="rp-td rp-td-accent rp-td-right">{EGP(r.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── GUESTS TAB ───────────────────────────────────────────────
function GuestsReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/guests`);
      const all: any[] = Array.isArray(res.data) ? res.data : res.data.data || [];
      const filtered = all.filter((g) =>
        from || to ? inRange(g.datestarted, from, to) : true
      );
      setRows(
        filtered.map((g) => ({
          Name: g.name,
          Phone: g.phoneNumber || "—",
          Email: g.email || "—",
          Source: g.source || "—",
          "Loyalty Points": g.loyaltyPoints ?? 0,
          VIP: g.isVip ? "Yes" : "No",
          Created: fmt(g.datestarted),
        }))
      );
      setGenerated(true);
    } catch {
      alert("Failed to fetch guests");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-report-body">
      <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      <button className="rp-generate-btn" onClick={generate} disabled={loading}>
        {loading ? "Generating…" : "Generate Report"}
      </button>

      {generated && (
        <>
          <div className="rp-summary-row">
            <div className="rp-summary-chip">
              <span className="rp-summary-label">Total Guests</span>
              <span className="rp-summary-val">{rows.length}</span>
            </div>
            <div className="rp-summary-chip">
              <span className="rp-summary-label">VIP Guests</span>
              <span className="rp-summary-val">{rows.filter((r) => r.VIP === "Yes").length}</span>
            </div>
            <button
              className="rp-export-btn"
              onClick={() => exportCSV("guests-report.csv", rows)}
              disabled={!rows.length}
            >
              Export CSV
            </button>
          </div>
          {rows.length === 0 ? (
            <div className="rp-empty">No guests found</div>
          ) : (
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr>
                    {["Name", "Phone", "Email", "Source", "Loyalty Points", "VIP", "Created"].map((h) => (
                      <th key={h} className="rp-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="rp-tr">
                      <td className="rp-td rp-td-bold">{r.Name}</td>
                      <td className="rp-td rp-td-muted">{r.Phone}</td>
                      <td className="rp-td rp-td-muted">{r.Email}</td>
                      <td className="rp-td">{r.Source}</td>
                      <td className="rp-td rp-td-accent">{r["Loyalty Points"]}</td>
                      <td className="rp-td">
                        {r.VIP === "Yes" ? <span className="rp-vip-badge">VIP</span> : <span className="rp-td-muted">—</span>}
                      </td>
                      <td className="rp-td rp-td-muted">{r.Created}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── OCCUPANCY TAB ─────────────────────────────────────────────
function OccupancyReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/dashboard/occupancy-heatmap`);
      const all: any[] = Array.isArray(res.data) ? res.data : res.data.days || [];
      const filtered = all.filter((d) =>
        from || to ? inRange(d.date, from, to) : true
      );
      setRows(
        filtered.map((d) => ({
          Date: fmt(d.date),
          "Booked Rooms": d.booked,
          "Total Rooms": d.total,
          "Occupancy %": d.pct ?? d.occupancy ?? 0,
        }))
      );
      setGenerated(true);
    } catch {
      alert("Failed to fetch occupancy data");
    } finally {
      setLoading(false);
    }
  };

  const avgOcc = rows.length
    ? Math.round(rows.reduce((s, r) => s + (r["Occupancy %"] || 0), 0) / rows.length)
    : 0;

  return (
    <div className="rp-report-body">
      <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      <button className="rp-generate-btn" onClick={generate} disabled={loading}>
        {loading ? "Generating…" : "Generate Report"}
      </button>

      {generated && (
        <>
          <div className="rp-summary-row">
            <div className="rp-summary-chip">
              <span className="rp-summary-label">Days</span>
              <span className="rp-summary-val">{rows.length}</span>
            </div>
            <div className="rp-summary-chip">
              <span className="rp-summary-label">Avg Occupancy</span>
              <span className="rp-summary-val">{avgOcc}%</span>
            </div>
            <button
              className="rp-export-btn"
              onClick={() => exportCSV("occupancy-report.csv", rows)}
              disabled={!rows.length}
            >
              Export CSV
            </button>
          </div>
          {rows.length === 0 ? (
            <div className="rp-empty">No occupancy data for this period</div>
          ) : (
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr>
                    {["Date", "Booked Rooms", "Total Rooms", "Occupancy %"].map((h) => (
                      <th key={h} className="rp-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="rp-tr">
                      <td className="rp-td rp-td-bold">{r.Date}</td>
                      <td className="rp-td">{r["Booked Rooms"]}</td>
                      <td className="rp-td rp-td-muted">{r["Total Rooms"]}</td>
                      <td className="rp-td rp-td-accent">{r["Occupancy %"]}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── MAIN PAGE ────────────────────────────────────────────────
const TABS: ReportTab[] = ["Bookings", "Revenue", "Guests", "Occupancy"];

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportTab>("Bookings");

  return (
    <DashboardLayout>
      <div className="rp-page">
        {/* Header */}
        <div className="rp-header">
          <div>
            <h1 className="rp-title">Reports</h1>
            <p className="rp-subtitle">Generate and export detailed reports</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="rp-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              className={`rp-tab ${activeTab === t ? "active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="rp-content-card">
          <div className="rp-content-header">
            <h2 className="rp-content-title">{activeTab} Report</h2>
            <p className="rp-content-sub">Select a date range and generate the report</p>
          </div>
          {activeTab === "Bookings"   && <BookingsReport />}
          {activeTab === "Revenue"    && <RevenueReport />}
          {activeTab === "Guests"     && <GuestsReport />}
          {activeTab === "Occupancy"  && <OccupancyReport />}
        </div>
      </div>
    </DashboardLayout>
  );
}
